import { Chip, useTheme, alpha } from "@mui/material";

/**
 * RolePill — accent-colored role chip with optional 'preview' badge.
 *
 * Six roles from the SCK-462 policy matrix. Until Phase 2 guards
 * enforce (see SCK-468/469), every pill renders with `preview` on
 * so the UI is honest about the current state: label, not permission.
 * Removing the preview badge is the visible signal that a role is
 * now truly enforced end-to-end.
 */

const ROLE_META = {
  OWNER:     { label: "Owner",     shade: "orange", weight: 700 },
  ADMIN:     { label: "Admin",     shade: "orange", weight: 600 },
  DEVELOPER: { label: "Developer", shade: "blue",   weight: 500 },
  VIEWER:    { label: "Viewer",    shade: "grey",   weight: 500 },
  BILLING:   { label: "Billing",   shade: "green",  weight: 500 },
  SYSTEM:    { label: "System",    shade: "ink",    weight: 700 },
};

function resolveColors(shade, theme) {
  const dark = theme.palette.mode === "dark";
  switch (shade) {
    case "orange":
      return {
        fill: dark ? "#fb923c" : "#ea580c",
        text: dark ? "#0b0f10" : "#ffffff",
      };
    case "blue":
      return {
        fill: dark ? "#60a5fa" : theme.palette.info.main,
        text: dark ? "#0b0f10" : "#ffffff",
      };
    case "green":
      return {
        fill: dark ? "#34d399" : theme.palette.success.main,
        text: dark ? "#0b0f10" : "#ffffff",
      };
    case "grey":
      return {
        fill: dark ? theme.palette.grey[500] : theme.palette.grey[600],
        text: dark ? "#0b0f10" : "#ffffff",
      };
    case "ink":
    default:
      return {
        fill: dark ? theme.palette.grey[100] : theme.palette.grey[900],
        text: dark ? "#0b0f10" : "#ffffff",
      };
  }
}

export default function RolePill({ role, preview = true, size = "small" }) {
  const theme = useTheme();
  const meta = ROLE_META[role] || ROLE_META.VIEWER;
  const { fill, text } = resolveColors(meta.shade, theme);

  return (
    <Chip
      size={size}
      label={
        <span
          style={{
            fontWeight: meta.weight,
            letterSpacing: "0.03em",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {meta.label}
          {preview && (
            <span
              title="This role is a label only until Phase 2 guards enforce it end-to-end."
              style={{
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "1px 5px",
                borderRadius: 3,
                background: alpha(text, 0.22),
                color: text,
                lineHeight: 1,
              }}
            >
              preview
            </span>
          )}
        </span>
      }
      sx={{
        backgroundColor: fill,
        color: text,
        borderRadius: 1,
        height: size === "small" ? 22 : 26,
        "& .MuiChip-label": {
          px: 1.2,
          py: 0,
        },
      }}
    />
  );
}
